import {
  AvroOptional,
  log as signalsLogRenderError,
} from "@indeed/signals-schema/com/indeed/signals/schema/reactnative/RNRenderErrorV5";
import React, { createContext, useCallback, useContext, useMemo } from "react";

import { getDeviceProperties } from "../../logging/LoggableDeviceProperties";
import { logHttpError as loghttpErrorInternal } from "../../logging/NetworkLogging";
import { sanitizeError } from "../../logging/RenderErrorBoundary";
import type { NativeContextRNProps } from "../../props/NativeContextRNProps";
import { getRnErrorProviderId } from "../../util/getRnErrorProviderId";

export interface ReportErrorContextValue {
  logError: (errorOrMessage: Error | string) => void;
  logHttpError: (
    url: string,
    message: string,
    statusCode: number,
    nativeContext: NativeContextRNProps | null | undefined,
  ) => void;
}

const ReportErrorContext = createContext<ReportErrorContextValue | undefined>(undefined);

export interface ReportErrorProviderProps {
  providerId: string;
  children: React.ReactNode;
}

export function ReportErrorProvider({ providerId, children }: ReportErrorProviderProps) {
  const logError = useCallback(
    (errorOrMessage: Error | string) => {
      const error = sanitizeError(errorOrMessage);
      const { message, name, stack = "" } = error;

      signalsLogRenderError({
        provider: AvroOptional.string(providerId),
        providerId: {
          ProviderId: getRnErrorProviderId(providerId),
        },
        deviceProperties: getDeviceProperties(),
        message,
        name,
        stack,
        componentStack: "",
      });
    },
    [providerId],
  );

  const logHttpError = useCallback(
    (
      url: string,
      message: string,
      statusCode: number,
      nativeContext: NativeContextRNProps | null | undefined,
    ) => {
      loghttpErrorInternal(providerId, url, message, statusCode, nativeContext);
    },
    [providerId],
  );

  const value = useMemo<ReportErrorContextValue>(
    () => ({ logError, logHttpError }),
    [logError, logHttpError],
  );

  return <ReportErrorContext.Provider value={value}>{children}</ReportErrorContext.Provider>;
}

export function useReportError(): ReportErrorContextValue {
  const ctx = useContext(ReportErrorContext);
  if (!ctx) {
    throw new Error("useReportError must be used within a ReportErrorProvider");
  }
  return ctx;
}

export type { ReportErrorContextValue as ReportErrorApi };
